/// <amd-module name="DistributedTaskUI/Logs/VerticalTab/VerticalTabList" />
import * as React from "react";

import { VerticalTabItem } from "DistributedTaskUI/Logs/VerticalTab/VerticalTabItem";
import { DefaultVerticalItemRenderer } from "DistributedTaskUI/Logs/VerticalTab/DefaultVerticalItemRenderer";
import { IVerticalTabItemProps } from "DistributedTaskUI/Logs/VerticalTab/VerticalTabItem.Types";
import { IVerticalTabListProps } from "DistributedTaskUI/Logs/VerticalTab/VerticalTabList.Types";

import { FocusZone, FocusZoneDirection } from "OfficeFabric/FocusZone";
import { List } from "OfficeFabric/List";
import { getId } from "VSS/Core/Util/Object";

import { css, KeyCode } from "VSSUI/Util";

import { Splitter, SplitterElementPosition } from "VSSUI/Components/Splitter";

import "VSS/LoaderPlugins/Css!DistributedTaskUI/Logs/VerticalTab/VerticalTabList";

const MaxLeftPaneInitialWidth = 640;
const DefaultWidthPercent = 20;
const DefaultWidth = 300;
const verticalTabItemContainerClassName = "vertical-tab-item";

/**
 *  Usage:
 *
 *   <VerticalTabList>
 *     <VerticalTabItem itemText="Tab1">
 *       <Label>Vertical Tab Panel #1</Label>
 *     </VerticalTabItem>
 *     <VerticalTabItem itemText="Tab2">
 *       <Label>Vertical Tab Panel #2</Label>
 *     </VerticalTabItem>
 *     <VerticalTabItem itemText="Tab3">
 *       <Label>Vertical Tab Panel #3</Label>
 *     </VerticalTabItem>
 *   </VerticalTabList>
 */

export interface IVerticalTabListState {
    items: IVerticalTabItemProps[];
    selectedKey: string;
    selectedTabId: string;
}

export class VerticalTabList extends React.Component<IVerticalTabListProps, IVerticalTabListState> {
    constructor(props: IVerticalTabListProps) {
        super(props);
        this._verticalTabId = getId("VerticalTab");
        this._initialWidth = this._getInitialWidth();
        this._isItemManuallySelected = false;

        const items: IVerticalTabItemProps[] = this._getItems(this.props);

        let selectedKey: string | undefined;

        if (this.props.selectedKey) {
            selectedKey = this.props.selectedKey;
        } else if (items.length) {
            for (let i = 0, itemCount = items.length; i < itemCount; i++) {
                if (items[i].isItemNonInteractive) {
                    continue;
                }
                else {
                    selectedKey = items[i].itemKey;
                    break;
                }
            }
        }

        if (selectedKey) {
            this.state = {
                items,
                selectedKey: selectedKey,
                selectedTabId: this._keyToTabIds[selectedKey]
            } as IVerticalTabListState;
        } else {
            this.state = { items: [] as IVerticalTabItemProps[] } as IVerticalTabListState;
        }
    }

    public componentDidMount() {
        setTimeout(() => {
            // We need to add delay here since office fabric list render items in async fashion
            this._selectTabItem(this.props.selectedKey || "");
        }, 0);
    }

    public componentDidUpdate(prevProps: IVerticalTabListProps) {
        if (prevProps.selectedKey !== this.props.selectedKey) {
            setTimeout(() => {
                // We need to add delay here since office fabric list render items in async fashion
                this._selectTabItem(this.props.selectedKey || "");
            }, 0);
        }
    }

    public componentWillReceiveProps(nextProps: IVerticalTabListProps) {
        const items: IVerticalTabItemProps[] = this._getItems(nextProps);

        this.setState((prevState, props) => {
            let selectedKey: string | undefined;
            // If manually selected, don't honor the new selected key if we are asked not to
            const dontHonorSelectedKey = this.props.persistManualSelection && this._isItemManuallySelected;
            if (!dontHonorSelectedKey && this._isKeyValid(nextProps.selectedKey)) {
                selectedKey = nextProps.selectedKey;
            }
            else if (this._isKeyValid(prevState.selectedKey)) {
                selectedKey = prevState.selectedKey;
            }
            else if (items.length) {
                selectedKey = items[0].itemKey;
            }

            if (selectedKey) {
                return {
                    items: items,
                    selectedKey,
                    selectedTabId: this._keyToTabIds[selectedKey],
                } as IVerticalTabListState;
            }
            else {
                return null;
            }
        });
    }

    public render(): JSX.Element {
        let left = (
            <div className={css("vertical-tablist-left-pane", this.props.leftClassName)}>
                <div className="vertical-tablist-left-section"
                    role="region"
                    data-is-scrollable={true}
                    aria-label={this.props.leftPaneARIARegionRoleLabel}
                    ref={(element) => this._verticalTabLeftSection = element}>
                    <FocusZone direction={FocusZoneDirection.vertical} isInnerZoneKeystroke={(keyEvent: React.KeyboardEvent<HTMLElement>) => (
                        (keyEvent.which === KeyCode.rightArrow))}>
                        <List
                            role="tablist"
                            className="vertical-tablist-items"
                            items={this.state.items}
                            onRenderCell={this._renderItem}
                            {...this.props.listProps}
                        />
                    </FocusZone>
                </div>
            </div>
        );

        const selectedTabItemProps = this._getSelectedTabItemProps();
        const isRightPaneFocusable = selectedTabItemProps && selectedTabItemProps.isRightPaneFocusable;
        let right = (
            <div className={css("vertical-tablist-right-pane", this.props.rightClassName)}>
                <div className="vertical-tablist-right-section"
                    role="region"
                    data-is-scrollable={true}
                    data-is-focusable={!!isRightPaneFocusable}
                    tabIndex={!!isRightPaneFocusable ? 0 : -1}
                    aria-label={this.props.rightPaneARIARegionRoleLabel}>
                    <div className={"vertical-tab-item-panel"} role="tabpanel" aria-labelledby={this.state.selectedTabId}>
                        {this._getVerticalTabPanelContent()}
                    </div>
                </div>
            </div>
        );

        return (

            <Splitter
                onRenderNearElement={() => left}
                onRenderFarElement={() => right}
                fixedElement={SplitterElementPosition.Near}
                className={css("vertical-tablist-splitter", this.props.cssClass)}
                initialFixedSize={this._initialWidth}
                maxFixedSize={(this.props.leftPaneMaxWidth && parseInt(this.props.leftPaneMaxWidth, 10)) || this._initialWidth * 2}
                minFixedSize={(this.props.leftPaneMinWidth && parseInt(this.props.leftPaneMinWidth, 10)) || (this._initialWidth < 250) ? this._initialWidth : 250}
            />
        );
    }

    /**
     * Gets the set of VerticalTabItem as array of IVerticalTabItemProps
     * The set of Vertical tab item is determined by child components of type VerticalTabItem
     */
    private _getItems(props: IVerticalTabListProps): IVerticalTabItemProps[] {
        const items: IVerticalTabItemProps[] = [];
        this._keyToIndexMapping = {};
        this._keyToTabIds = {};

        React.Children.map(props.children, (child: any, index: number) => {
            if (this._isVerticalTabItem(child)) {
                const item = child as VerticalTabItem;
                const itemKey = item.props.itemKey || index.toString();

                items.push({
                    itemText: item.props.itemText,
                    ariaProps: item.props.ariaProps,
                    ariaHidden: item.props.ariaHidden,
                    itemKey: itemKey,
                    item: item.props.item,
                    iconProps: item.props.iconProps,
                    statusIconProps: item.props.statusIconProps,
                    onRenderItem: item.props.onRenderItem,
                    itemFooterText: item.props.itemFooterText,
                    itemFooterTooltipContent: item.props.itemFooterTooltipContent,
                    isItemNonInteractive: item.props.isItemNonInteractive,
                    cssClass: item.props.cssClass,
                    isRightPaneFocusable: item.props.isRightPaneFocusable
                });
                this._keyToIndexMapping[itemKey] = index;
                this._keyToTabIds[itemKey] = this._getTabId(itemKey, index);
            }
        });

        return items;
    }

    private _getSelectedTabItemProps(): IVerticalTabItemProps {
        const index = this._keyToIndexMapping[this.state.selectedKey];
        return this.state.items[index];
    }

    private _getVerticalTabPanelContent(): React.ReactNode {
        const index = this._keyToIndexMapping[this.state.selectedKey];
        return React.Children.toArray(this.props.children as React.ReactChild[])[index];
    }

    private _renderItem = (item: IVerticalTabItemProps)  => { 
        const { itemKey, ariaProps, onRenderItem, cssClass } = item;
        const tabId = this._keyToTabIds[itemKey as string];
        let itemContent: JSX.Element | null;

        const isSelectedClassName = this.state.selectedKey === itemKey ? "is-selected" : "";
        const position = this._keyToIndexMapping[itemKey] + 1; // Index is starting with 0, hence adding 1
        const sizeOfSet = this.state.items && this.state.items.length;

        const computedAriaProps = {
            "aria-posinset": position,
            "aria-setsize": sizeOfSet
        };

        if (onRenderItem) {
            item.ariaProps = { ...ariaProps, ...computedAriaProps };
            itemContent = onRenderItem(item, this._renderItemContent);
        } else {
            itemContent = this._renderItemContent(item);
        }

        return (
            <div data-is-focusable={!item.isItemNonInteractive}
                id={tabId}
                key={itemKey}
                className={css(verticalTabItemContainerClassName, cssClass, isSelectedClassName)}
                onClick={this._onItemClick.bind(this, itemKey, item)}
                onKeyPress={this._onKeyPress.bind(this, itemKey, item)}
                role="tab"
                aria-selected={this.state.selectedKey === itemKey}
                {...ariaProps}
                {...computedAriaProps}
            >
                {itemContent}
            </div >
        );
    }

    private _renderItemContent = (item: IVerticalTabItemProps): JSX.Element  => { 
        return (
            <DefaultVerticalItemRenderer
                iconProps={item.iconProps}
                statusIconProps={item.statusIconProps}
                title={item.itemText}
                footerText={item.itemFooterText}
                footerTooltipContent={item.itemFooterTooltipContent}
                ariaHidden={item.ariaHidden}
            />);
    }

    private _isVerticalTabItem(item: any) {
        const child = item as VerticalTabItem;
        return child && typeof child === "object" && ((child as any).type === VerticalTabItem || child.props.isVerticalTabItem);
    }

    private _onItemClick(itemKey: string, item: IVerticalTabItemProps, ev: React.MouseEvent<HTMLElement>) {
        if (!item.isItemNonInteractive) {
            ev.preventDefault();
            this._updateSelectedItem(itemKey, ev);
        }
    }

    private _onKeyPress(itemKey: string, item: IVerticalTabItemProps, ev: React.KeyboardEvent<HTMLElement>) {
        if (ev.which === KeyCode.enter && !item.isItemNonInteractive) {
            ev.preventDefault();
            this._updateSelectedItem(itemKey);
        }
    }

    /**
     * Updates the state with the new selected index
     */
    private _updateSelectedItem(itemKey: string, ev?: React.MouseEvent<HTMLElement>) {
        this.setState({
            items: this.state.items.slice(),
            selectedKey: itemKey,
            selectedTabId: this._keyToTabIds[itemKey]
        } as IVerticalTabListState);

        if (this.props.onItemClick && this._keyToIndexMapping[itemKey] >= 0) {
            const index = this._keyToIndexMapping[itemKey];

            // React.Element<any> cannot directly convert to VerticalTabItem.
            const item = React.Children.toArray(this.props.children)[index] as any;

            if (this._isVerticalTabItem(item)) {
                this.props.onItemClick(item as VerticalTabItem, ev);
            }
        }

        this._isItemManuallySelected = true;
    }

    /**
     * whether the key exists in the tab items.
     */
    private _isKeyValid(itemKey: string | undefined) {
        return itemKey !== undefined && this._keyToIndexMapping[itemKey] !== undefined;
    }

    /**
     * Generates the Id for the tab button.
     */
    private _getTabId(itemKey: string, index: number): string {
        if (this.props.getTabId) {
            return this.props.getTabId(itemKey, index);
        }

        return this._verticalTabId + `-Tab${index}`;
    }

    private _getInitialWidth(): number {
        let widthInPx: number = DefaultWidth;
        let widthInPercent: number = DefaultWidthPercent;

        if (this.props.leftPaneInitialWidth) {
            if (this.props.leftPaneInitialWidth.endsWith("%")) {
                widthInPercent = parseInt(this.props.leftPaneInitialWidth, 10);
            } else {
                widthInPx = parseInt(this.props.leftPaneInitialWidth, 10);
            }
        }

        let outerWidth = 0;
        try {
            outerWidth = window.outerWidth;
        }
        catch (e) {
            console.error("window.outerWidth threw exception " + e);
        }

        let desiredWidth = widthInPx || (outerWidth * (widthInPercent || DefaultWidthPercent)) / 100 || DefaultWidth;
        return (this.props.leftPaneInitialWidth) ? desiredWidth : Math.min(MaxLeftPaneInitialWidth, desiredWidth);
    }

    private _selectTabItem(key: string) {
        const items = this.state.items;
        // scroll left pane item into view
        // If any item manually selected then don't scroll left pane into view
        if (items && items.length > 0 && !this._isItemManuallySelected && this._verticalTabLeftSection) {
            const selectedItemIndex = items.findIndex((item) => {
                return item.itemKey === key;
            });
            if (selectedItemIndex >= 0) {
                const components = this._verticalTabLeftSection.getElementsByClassName(verticalTabItemContainerClassName);
                if (components && components.length > selectedItemIndex) {
                    const component = components[selectedItemIndex] as HTMLElement;
                    if (component) {
                        window.requestAnimationFrame(() => {
                            component.scrollIntoView({ block: "end", behavior: "smooth" });
                        });
                    }
                }
            }
        }
    }

    private _initialWidth: number;
    private _verticalTabId: string;
    private _keyToIndexMapping: { [key: string]: number };
    private _keyToTabIds: { [key: string]: string };
    private _verticalTabLeftSection: HTMLElement | null;
    private _isItemManuallySelected: boolean;
}
